/**
 * React Query hooks for the RunDebugDrawer.
 *
 * - `useRunDebug` — aggregator; powers the Overview tab and seeds other tabs.
 * - `useRunTelemetryLogs` — lazy proxy fetch for superadmin Sentry tab.
 * - `useRunTelemetryTrace` — lazy proxy fetch for superadmin Sentry tab.
 */
import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../../../../../config';

export type RunDebugStep = {
  id: string;
  nodeId: string;
  name: string | null;
  status: string;
  startedAt: string | null;
  finishedAt: string | null;
  durationMs: number | null;
  cacheHitSource: string | null;
  cacheOutcome: string | null;
  cacheProcessingStatus: string | null;
  errorMessage: string | null;
  screenshotS3Key: string | null;
};

export type RunDebugPayload = {
  run: {
    id: string;
    workflowId: string;
    status: string;
    startedAt: string | null;
    finishedAt: string | null;
    durationMs: number | null;
    errorMessage: string | null;
    traceStorageKey: string | null;
  };
  steps: RunDebugStep[];
  cache: {
    hitRate: number;
    hitCount: number;
    missCount: number;
    bySource: Record<string, number>;
    misses: Array<{ stepId: string; reason: string }>;
  };
  thoughts: { count: number; byStepId: Record<string, number> };
  playwright: { viewerUrl: string | null; traceStorageKey: string | null };
  sentry: { logsAvailable: boolean; tracesAvailable: boolean };
  diagnosis: {
    slowestSteps: Array<{ stepId: string; name: string | null; durationMs: number }>;
    failedSteps: Array<{ stepId: string; name: string | null; errorMessage: string | null }>;
    cacheMissHotspots: Array<{ stepId: string; reason: string }>;
  };
  repro?: {
    available: boolean;
    unavailableReason:
      | 'no_sandbox'
      | 'environment_removed'
      | 'not_running'
      | 'expired'
      | 'no_public_url'
      | null;
    sandbox: {
      id: string;
      publicUrl: string | null;
      status: string;
      expiresAt: string | null;
    } | null;
    credentials: Array<{
      id: string;
      propertyId: string;
      name: string;
      role: string | null;
      authProvider: string | null;
      credentialTemplateId: string | null;
    }>;
  };
};

export function useRunDebug(
  workflowId: string | undefined,
  runId: string | undefined,
  enabled = true
) {
  return useQuery<RunDebugPayload>({
    queryKey: ['workflow-run-debug', workflowId, runId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/workflows/${workflowId}/runs/${runId}/debug`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to load run debug data');
      const json = await res.json();
      if (!json.ok || !json.data) throw new Error(json.error ?? 'Failed to load run debug data');
      return json.data as RunDebugPayload;
    },
    enabled: enabled && Boolean(workflowId) && Boolean(runId),
    // Repro availability is time-sensitive and can change when retention
    // expires or a rerun extends it.
    staleTime: 10_000,
    refetchInterval: 30_000,
  });
}

export type RunTelemetryLog = {
  timestamp: string;
  level: string;
  message: string;
  traceId: string | null;
  stepId: string | null;
  attributes: Record<string, unknown>;
};

export type RunTelemetryLogsPayload = {
  rows: RunTelemetryLog[];
  cursor: string | null;
  partial: boolean;
  reason: string | null;
};

export type RunNavigationEntry = {
  timestampMs: number;
  isoTimestamp: string | null;
  url: string;
  hostname: string | null;
  success: boolean;
};

export type RunNavigationTransition = {
  fromIndex: number;
  toIndex: number;
  fromHostname: string | null;
  toHostname: string | null;
  fromUrl: string;
  toUrl: string;
  timestampMs: number;
};

export type RunNavigationPayload = {
  startTimestamp: string | null;
  entries: RunNavigationEntry[];
  transitions: RunNavigationTransition[];
};

export function useRunNavigation(
  workflowId: string | undefined,
  runId: string | undefined,
  enabled = true
) {
  return useQuery<RunNavigationPayload>({
    queryKey: ['workflow-run-debug-navigation', workflowId, runId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/workflows/${workflowId}/runs/${runId}/debug/navigation`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to load navigation history');
      const json = await res.json();
      if (!json.ok || !json.data)
        throw new Error(json.error ?? 'Failed to load navigation history');
      return json.data as RunNavigationPayload;
    },
    enabled: enabled && Boolean(workflowId) && Boolean(runId),
    staleTime: 30_000,
  });
}

export function useRunTelemetryLogs(
  workflowId: string | undefined,
  runId: string | undefined,
  opts: { enabled: boolean; level?: string; stepId?: string }
) {
  const params = new URLSearchParams();
  if (opts.level) params.set('level', opts.level);
  if (opts.stepId) params.set('step', opts.stepId);
  const qs = params.toString();
  return useQuery<RunTelemetryLogsPayload>({
    queryKey: ['workflow-run-telemetry-logs', workflowId, runId, opts.level, opts.stepId],
    queryFn: async () => {
      const res = await fetch(
        `${API_URL}/workflows/${workflowId}/runs/${runId}/debug/logs${qs ? `?${qs}` : ''}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('Failed to load telemetry logs');
      const json = await res.json();
      return json.data as RunTelemetryLogsPayload;
    },
    enabled: opts.enabled && Boolean(workflowId) && Boolean(runId),
    staleTime: 30_000,
  });
}

export type RunTelemetrySpan = {
  spanId: string;
  parentSpanId: string | null;
  op: string | null;
  description: string | null;
  startTimestamp: string;
  endTimestamp: string | null;
  durationMs: number | null;
  status: string | null;
  attributes: Record<string, unknown>;
};

export type RunTelemetryTracePayload = {
  traceId: string | null;
  spans: RunTelemetrySpan[];
  partial: boolean;
  reason: string | null;
};

export function useRunTelemetryTrace(
  workflowId: string | undefined,
  runId: string | undefined,
  opts: { enabled: boolean; stepId?: string }
) {
  const qs = opts.stepId ? `?step=${encodeURIComponent(opts.stepId)}` : '';
  return useQuery<RunTelemetryTracePayload>({
    queryKey: ['workflow-run-telemetry-trace', workflowId, runId, opts.stepId],
    queryFn: async () => {
      const res = await fetch(
        `${API_URL}/workflows/${workflowId}/runs/${runId}/debug/sentry-trace${qs}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('Failed to load telemetry trace');
      const json = await res.json();
      return json.data as RunTelemetryTracePayload;
    },
    enabled: opts.enabled && Boolean(workflowId) && Boolean(runId),
    staleTime: 30_000,
  });
}
